/* ==========================================================================
   FILE: services/analytics.service.js
   IAMONEAI - Analytics Service
   Batch event tracking and usage stats
   ========================================================================== */

import { client } from '../api';

// ==========================================================================
// CONFIGURATION
// ==========================================================================

const CONFIG = {
  batchSize: 100,
  flushInterval: 30000, // 30 seconds
  storageKey: 'iamoneai_analytics_queue',
};

// ==========================================================================
// EVENT QUEUE
// ==========================================================================

let eventQueue = [];
let flushTimer = null;
let sessionId = null;

// Generate session ID
function getSessionId() {
  if (!sessionId) {
    sessionId = `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  return sessionId;
}

// Load queue from storage
function loadQueue() {
  try {
    const stored = localStorage.getItem(CONFIG.storageKey);
    if (stored) {
      eventQueue = JSON.parse(stored);
    }
  } catch (err) {
    eventQueue = [];
  }
}

// Save queue to storage
function saveQueue() {
  try {
    localStorage.setItem(CONFIG.storageKey, JSON.stringify(eventQueue));
  } catch (err) {
    // Storage full - clear old events
    eventQueue = eventQueue.slice(-50);
  }
}

// Initialize
if (typeof window !== 'undefined') {
  loadQueue();
}

// ==========================================================================
// ANALYTICS SERVICE
// ==========================================================================

const analyticsService = {
  /**
   * Track an event
   * @param {string} type - Event type
   * @param {Object} data - Event data
   * @param {string} iin - User's IIN (optional)
   */
  track(type, data = {}, iin = null) {
    const event = {
      type,
      data,
      iin,
      timestamp: Date.now(),
      sessionId: getSessionId(),
    };

    eventQueue.push(event);
    saveQueue();

    // Auto-flush if batch size reached
    if (eventQueue.length >= CONFIG.batchSize) {
      this.flush();
    }

    // Start flush timer if not running
    this.startFlushTimer();
  },

  /**
   * Start periodic flush timer
   */
  startFlushTimer() {
    if (flushTimer) return;

    flushTimer = setInterval(() => {
      if (eventQueue.length > 0) {
        this.flush();
      }
    }, CONFIG.flushInterval);
  },

  /**
   * Stop flush timer
   */
  stopFlushTimer() {
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
  },

  /**
   * Flush events to server
   * @returns {Promise<Object>}
   */
  async flush() {
    if (eventQueue.length === 0) {
      return { sent: 0 };
    }

    const eventsToSend = [...eventQueue];
    eventQueue = [];
    saveQueue();

    try {
      await client.post('/analytics/batch', {
        events: eventsToSend,
        sessionId: getSessionId(),
      }, {
        cache: false,
        retry: true,
        priority: 'low',
        offline: 'queue',
      });

      return { sent: eventsToSend.length };
    } catch (err) {
      // Put events back in queue on failure
      eventQueue = [...eventsToSend, ...eventQueue].slice(0, CONFIG.batchSize * 2);
      saveQueue();
      throw err;
    }
  },

  /**
   * Get usage statistics
   * @param {string} iin 
   * @param {string} period - Month in YYYY-MM format
   * @returns {Promise<Object>}
   */
  async getUsage(iin, period = null) {
    const currentPeriod = period || new Date().toISOString().slice(0, 7);
    
    return client.get(`/analytics/${iin}?period=${currentPeriod}`, {
      cache: true,
      cacheTTL: 60000, // 1 minute
    });
  },

  /**
   * Get queue size
   * @returns {number}
   */
  getQueueSize() {
    return eventQueue.length;
  },

  // ==========================================================================
  // CONVENIENCE TRACKING METHODS
  // ==========================================================================

  pageView(page, iin = null) {
    this.track('page_view', { page }, iin);
  },

  login(iin) {
    this.track('login', {}, iin);
  },

  logout(iin) {
    this.track('logout', {}, iin);
  },

  chatSend(messageLength, iin = null) {
    this.track('chat_send', { messageLength }, iin);
  },

  chatReceive(responseLength, blocked = false, iin = null) {
    this.track('chat_receive', { responseLength, blocked }, iin);
  },

  memoryAdd(type, iin = null) {
    this.track('memory_add', { type }, iin);
  },

  goalCreate(category, iin = null) {
    this.track('goal_create', { category }, iin);
  },

  goalComplete(category, iin = null) {
    this.track('goal_complete', { category }, iin);
  },

  contextSwitch(from, to, iin = null) {
    this.track('context_switch', { from, to }, iin);
  },

  error(errorType, message, iin = null) {
    this.track('error', { errorType, message }, iin);
  },
};

// ==========================================================================
// PAGE UNLOAD - Flush remaining events
// ==========================================================================

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    analyticsService.flush();
  });

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      analyticsService.flush();
    }
  });
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export { analyticsService };
export default analyticsService;
